import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingDown, Package, Box } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PlanningDashboard = () => {
  const navigate = useNavigate();

  // Mock data for issues
  const criticalRawMaterials = [
    { sku: "RM-GB-001", name: "Ground Beef", currentStock: 650, weeksOnHand: 0.8, issue: "Critical - Less than 1 week" },
    { sku: "RM-GC-004", name: "Green Chili", currentStock: 420, weeksOnHand: 1.7, issue: "Low Stock - 1-3 weeks" },
    { sku: "RM-BA-005", name: "Bacon", currentStock: 370, weeksOnHand: 1.2, issue: "Low Stock - 1-3 weeks" }
  ];

  const criticalFinishedGoods = [
    { sku: "EVOL-281", name: "Egg and Green Chili Burrito", currentStock: 350, weeksOnHand: 0.6, issue: "Critical - Less than 1 week" },
    { sku: "MCF-296", name: "Bacon Egg and Cheese Burrito", currentStock: 530, weeksOnHand: 0.8, issue: "Critical - Less than 1 week" }
  ];

  const overstockedItems = [
    { sku: "DH-D04-FALL", name: "Daily Harvest Bowl - Fall Harvest", currentStock: 7200, weeksOnHand: 5.1, type: "Finished Goods" },
    { sku: "EVOL-293", name: "Steak Burrito", currentStock: 8500, weeksOnHand: 7.1, type: "Finished Goods" }
  ];

  const negativeProjections = [
    { sku: "EVOL-281", name: "Egg and Green Chili Burrito", week: 12, projectedBalance: -50, type: "Finished Goods" },
    { sku: "RM-FT-002", name: "Flour Tortillas", week: 4, projectedBalance: 1050, intraDayNegative: true, type: "Raw Materials" }
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning Dashboard</h1>
        <p className="text-gray-600">Inventory issues requiring attention</p>
      </div>

      {/* Critical Stock Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Critical Raw Materials */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Critical Raw Materials
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {criticalRawMaterials.map((item, idx) => (
                <div 
                  key={idx} 
                  className="border-l-4 border-red-500 bg-red-50 p-3 rounded cursor-pointer hover:bg-red-100"
                  onClick={() => navigate('/planning/raw-materials')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900">{item.sku}</span>
                    <Badge className="bg-red-100 text-red-800">{item.weeksOnHand.toFixed(1)}w</Badge>
                  </div>
                  <div className="text-sm text-gray-700">{item.name}</div>
                  <div className="text-xs text-red-700 mt-1">{item.issue}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Critical Finished Goods */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Critical Finished Goods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {criticalFinishedGoods.map((item, idx) => (
                <div 
                  key={idx} 
                  className="border-l-4 border-red-500 bg-red-50 p-3 rounded cursor-pointer hover:bg-red-100"
                  onClick={() => navigate('/planning/finished-goods')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900">{item.sku}</span>
                    <Badge className="bg-red-100 text-red-800">{item.weeksOnHand.toFixed(1)}w</Badge>
                  </div>
                  <div className="text-sm text-gray-700">{item.name}</div>
                  <div className="text-xs text-red-700 mt-1">{item.issue}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overstocked Items */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingDown className="w-5 h-5 text-blue-600" />
            Overstocked Items (5+ weeks on hand)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {overstockedItems.map((item, idx) => (
              <div 
                key={idx} 
                className="border-l-4 border-blue-500 bg-blue-50 p-3 rounded cursor-pointer hover:bg-blue-100"
                onClick={() => navigate(item.type === "Finished Goods" ? '/planning/finished-goods' : '/planning/raw-materials')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">{item.sku}</span>
                  <Badge className="bg-blue-100 text-blue-800">{item.weeksOnHand.toFixed(1)}w</Badge>
                </div>
                <div className="text-sm text-gray-700">{item.name}</div>
                <div className="text-xs text-blue-700 mt-1">Type: {item.type}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Negative Projections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5 text-orange-600" />
            Projected Stockouts (Next 13 Weeks)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {negativeProjections.map((item, idx) => (
              <div 
                key={idx} 
                className="border-l-4 border-orange-500 bg-orange-50 p-3 rounded cursor-pointer hover:bg-orange-100"
                onClick={() => navigate(item.type === "Finished Goods" ? '/planning/finished-goods' : '/planning/raw-materials')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">{item.sku}</span>
                  <Badge className="bg-orange-100 text-orange-800">Week {item.week}</Badge>
                </div>
                <div className="text-sm text-gray-700">{item.name}</div>
                {item.intraDayNegative ? (
                  <div className="text-xs text-orange-700 mt-1">
                    ⚠️ Goes negative during week (PO expected later)
                  </div>
                ) : (
                  <div className="text-xs text-orange-700 mt-1">
                    Projected Balance: {item.projectedBalance} (Negative)
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanningDashboard;
